import { useTheme } from "@mui/material";
import { ResponsiveChoropleth } from "@nivo/geo";
import coordinates from "../../../assets/coordinates/features.json";
import useResponsive from "~src/hooks/useResponsive";

type MarketCategory = "established" | "growing" | "opportunity";

// Define a modified version of ChoroplethProps with optional features
interface ModifiedChoroplethProps {
  data: Array<{ id: string; value: number; category?: MarketCategory; color?: string }>;
  features?: typeof coordinates.features;
  margin?: { top: number; right: number; bottom: number; left: number };
  colors?: string[];
  domain: number[];
  unknownColor?: string;
  label?: string;
  valueFormat?: string;
  projectionScale?: number;
  projectionRotation?: [number, number, number];
  enableGraticule?: boolean;
  borderWidth?: number;
  borderColor?: string;
  [key: string]: any;
}

// Create a wrapper component that uses default parameters instead of defaultProps
const ChoroplethWrapper = ({
  data,
  features = coordinates.features,
  margin = { top: 0, right: 0, bottom: 0, left: 0 },
  colors,
  domain,
  unknownColor,
  label = "properties.name",
  valueFormat = ".2s",
  projectionScale,
  projectionRotation = [0, 0, 0] as [number, number, number],
  enableGraticule = false,
  borderWidth = 0.5,
  borderColor,
  ...rest
}: ModifiedChoroplethProps) => (
  <ResponsiveChoropleth
    data={data}
    features={features}
    margin={margin}
    colors={colors}
    domain={domain}
    unknownColor={unknownColor}
    label={label}
    valueFormat={valueFormat}
    projectionScale={projectionScale}
    projectionRotation={projectionRotation}
    enableGraticule={enableGraticule}
    borderWidth={borderWidth}
    borderColor={borderColor}
    {...rest}
  />
);

export default function TrafficGeoGraph({
  graphData,
}: {
  graphData: {
    established: { id: string; value: number }[];
    growing: { id: string; value: number }[];
    opportunity: { id: string; value: number }[];
  };
}) {
  const theme = useTheme();
  const isDesktop = useResponsive("up", "lg");

  const combinedData = [
    ...graphData.established.map((item) => ({
      ...item,
      category: "established" as MarketCategory,
      color: "#7AA5D3",
    })),
    ...graphData.growing.map((item) => ({
      ...item,
      category: "growing" as MarketCategory,
      color: "#43D2E9",
    })),
    ...graphData.opportunity.map((item) => ({
      ...item,
      category: "opportunity" as MarketCategory,
      color: "#A24AF0",
    })),
  ];

  const buffer = 2;

  const lowestEstablishedValue =
    Math.min(...graphData.established.map((item) => item.value)) - buffer;

  const highestEstablishedValue =
    Math.max(...graphData.established.map((item) => item.value)) + buffer;

  const lowestGrowingValue =
    Math.min(...graphData.growing.map((item) => item.value)) - buffer;

  const highestGrowingValue =
    Math.max(...graphData.growing.map((item) => item.value)) + buffer;

  const lowestOpportunityValue =
    Math.min(...graphData.opportunity.map((item) => item.value)) - buffer;

  const highestOpportunityValue =
    Math.max(...graphData.opportunity.map((item) => item.value)) + buffer;

  return (
    <ChoroplethWrapper
      data={combinedData}
      colors={["#A24AF0", "#43D2E9", "#7AA5D3"]}
      domain={[
        lowestOpportunityValue,
        highestOpportunityValue,
        lowestGrowingValue,
        highestGrowingValue,
        lowestEstablishedValue,
        highestEstablishedValue,
      ]}
      unknownColor={theme.palette.grey[100]}
      projectionScale={isDesktop ? 90 : 50}
      borderColor={theme.palette.grey[400]}
    />
  );
}
